// SZEGMENTÁLT VÁLASZTÓ (segmented control) — kölcsönösen kizáró opciók EGY sávban: halvány konténer, a
// kiválasztott szegmens „kiemelkedik" (felület-színű lap + finom árnyék).
//
// MIÉRT EZ A PRIMITÍV: a jogosultság-konzol „Tag hozzáadása" dialógusában ez a megjelenés bevált (Munkatárs /
// Külsős), és a user az ÜZENET-ÍRÁS típus-/címzés-választóira is ezt kérte a korábbi, telített
// zöld pillek helyett. A telített zöld ott félrevezető volt: az „elsődleges akció" jelentését vitte egy
// SZŰRŐ-szerű választásra. Két hely = közös primitív (a dialógus inline gombjai is erre cserélve).
//
// A11Y: `role="radiogroup"` + `role="radio"` + `aria-checked` (a `PillField` bevált mintája) — így a
// képernyőolvasó egy választó-csoportot mond, és a tesztek is a szemantikára kérdezhetnek, nem az osztályokra.
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /**
   * Opcionális DARABSZÁM-chip a felirat után (a partner-portál fül-sávjának mintája). MIÉRT ITT: a szegmens
   * jelentése „nézet-váltás", és a váltás előtt hasznos tudni, mennyi van a másik oldalon — enélkül a
   * felhasználó vaktában kattint. A `undefined` → nincs chip (a korábbi viselkedés).
   */
  count?: number;
}

export interface SegmentedProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SegmentedOption[];
  /** A csoport képernyőolvasó-neve (a mező címkéje). */
  'aria-label'?: string | undefined;
  disabled?: boolean | undefined;
  /** A KONTÉNER extra osztályai — pl. `grid grid-cols-2` az egyenlő szélességű felezéshez. */
  className?: string | undefined;
}

export function Segmented({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  disabled = false,
  className,
}: SegmentedProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      // `flex w-fit` (NEM `inline-flex`): a mező-címke `<label>` INLINE elem — egy inline-flex sáv mellé
      // sodródna ugyanabba a sorba (mérve: „Típus [Belső][Külső]" egy sorban). A blokk-szintű, tartalom-
      // szélességű sáv a címke ALÁ kerül, ahogy minden más mező-vezérlő.
      className={cn('bg-muted/40 flex w-fit flex-wrap gap-1 rounded-lg p-1', className)}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'ease-atlas focus-visible:ring-accent-600 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.icon}
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  'ml-0.5 rounded-full px-1.5 text-[10.5px] font-semibold leading-[1.45]',
                  active
                    ? 'bg-brand-soft text-brand-dark dark:text-brand'
                    : 'bg-card text-muted-foreground',
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
